/* After the APO widget moves a step — forward on "Next Step" or back on
   "Back" — settle the top
   of the product info column (title, price, then the steps) just below the
   sticky header — the whole column, title and price included, stays visible.

   Constraints measured against the live store:
   - The app's own "scroll to opened step" (scroll_to_opened_step, on by
     default) eases scrollingElement.scrollTop towards ITS target for ~1s and
     tears down any scroll issued meanwhile. Concurrent writes are the
     tug-of-war users saw — so scroll ONLY while things have been quiet,
     and re-fire after every later scuffle until aligned.
   - An "on-scroll-up" header slides back over the page on ANY upward scroll
     (this correction included), so it reserves its band like the
     always-visible sticky types.
   - The user's own scrolling is king: gestures abort the guards. */
(function () {
  'use strict';
  if (window.LPApoStepScroll) return;
  window.LPApoStepScroll = true;

  const NEXT = '.mw-group__button-next';
  const BACK = '.mw-group__button-back';
  const LAST_STEP = '.mw-group__button-next--addtocart, .mw-group__button-next--buy-now';
  const EXCLUDED = 'quick-add-modal, .mw-tingle';
  const QUIET_MS = 300;        // no movement for this long = safe to act
  const MOVE_EPSILON = 1.5;    // subpixel twitch below this counts as quiet
  const MIN_LEAD_MS = 350;     // the app starts easing ~200ms after a click
  const OUR_COOLDOWN_MS = 900; // min gap between two scrolls issued by us
  const USER_SETTLE_MS = 1300; // stand down while the user is steering
  const TOLERANCE = 7;
  const MAX_MOVES = 8;
  const SESSION_MS = 8000;
  const scroller = document.scrollingElement || document.documentElement;

  const pending = { index: -1, container: null, observer: null, timer: 0, goesBack: false };
  let session = null;

  // The app keeps every step mounted and toggles the mw-group__hidden class,
  // so the visible index is the ground truth for a step change.
  function activeIndex(nodes) {
    for (let index = 0; index < nodes.length; index += 1) {
      const group = nodes[index];
      if (!group.classList.contains('mw-group__hidden') && !group.hidden &&
          group.getAttribute('aria-hidden') !== 'true') return index;
    }
    return -1;
  }
  // --header-height is maintained by sections/header.liquid.
  function reservedOffset() {
    const header = document.querySelector('.header-wrapper');
    if (header && ['always', 'reduce-logo-size', 'on-scroll-up']
        .includes(header.getAttribute('data-sticky-type'))) {
      const height = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
      return 12 + (isNaN(height) ? header.offsetHeight : height);
    }
    return 12;
  }

  function poll() {
    if (!session) return;
    const now = performance.now();
    const y = scroller.scrollTop;
    if (Math.abs(y - session.lastY) > MOVE_EPSILON) {
      session.lastY = y;
      session.movedAt = now; // something is animating: app burst, glide, keyboard
    }
    if (now - session.movedAt < QUIET_MS) return; // never race a moving scroll
    const age = now - session.flipsAt;
    if (age < MIN_LEAD_MS) return;
    if (age > SESSION_MS || session.ourMoves >= MAX_MOVES) { abortSession(); return; }
    if (session.ourMoves > 0 && now - session.ourLastAt < OUR_COOLDOWN_MS) return;
    if (!session.container.isConnected) { abortSession(); return; }
    // Re-address the live DOM each poll: the app re-renders its groups, so the
    // elements seen at the flip can be stale yards later.
    const visible = activeIndex(session.container.querySelectorAll(':scope > .mw-group'));
    if (visible < 0) return; // mid-transition: re-read on the next poll
    // Anchor = the product info column top (title, then price) so the whole
    // column — not just the step group — shows below the header on a step
    // change. scrollIntoView() is unreliable: the column carries a slide-in
    // transform and has a sticky top rule, though it flows normally in this
    // theme's grid (measured doc top constant across scroll depths).
    const anchor = session.container.closest('.product__info-wrapper') || session.container;
    const offset = reservedOffset();
    const top = anchor.getBoundingClientRect().top;
    if (Math.abs(top - offset) <= TOLERANCE) {
      // Aligned: reconfirm across two polls (fresh images can shift heights).
      if (++session.okPolls >= 2) abortSession();
      return;
    }
    session.okPolls = 0;
    if (now - session.lastUserAt < USER_SETTLE_MS) return;
    const limit = Math.max(0, scroller.scrollHeight - window.innerHeight);
    const target = Math.min(Math.max(0, top + y - offset), limit);
    // First correction glides; later repairs snap so nothing tears them apart.
    window.scrollTo({
      top: target,
      behavior: session.ourMoves === 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
    });
    session.ourMoves += 1;
    session.ourLastAt = now;
    session.movedAt = now; // our glide keeps the quiet gate busy until it ends
  }

  function startSession(container, flipsAt) {
    abortSession();
    session = {
      container, flipsAt, lastY: scroller.scrollTop, movedAt: flipsAt,
      ourMoves: 0, ourLastAt: -1e9, lastUserAt: -1e9, okPolls: 0,
    };
    session.interval = setInterval(poll, 60);
  }
  function abortSession() {
    if (!session) return;
    clearInterval(session.interval);
    session = null;
  }

  // Gestures after our first move abort the guards for good; earlier ones
  // only postpone the pending correction until the user settles.
  function onUserIntent() {
    if (!session) return;
    if (session.ourMoves > 0) { abortSession(); return; }
    session.lastUserAt = performance.now();
  }
  ['wheel', 'touchstart', 'touchmove'].forEach(type =>
    window.addEventListener(type, onUserIntent, { passive: true, capture: true }));

  // Gating on the visible group index instead of the click keeps this correct
  // for LPProductEmail, which can stop a Next-click in the capture phase for
  // an email check and re-trigger the button; failed validation never flips
  // the classes, so nothing scrolls.
  // goesBack selects the bound: a Back click both cancels anything pending
  // (watch disarms first) and aligns the return flip like a forward one.
  function watch(index, container, goesBack) {
    disarm();
    pending.index = index;
    pending.container = container;
    pending.observer = new MutationObserver(() => {
      const visible = activeIndex(container.querySelectorAll(':scope > .mw-group'));
      if (goesBack ? visible <= pending.index - 1 : visible >= pending.index + 1) {
        stop();
        startSession(container, performance.now());
      }
    });
    // Unfiltered: the app's step flip rides along with deep option mutations
    // (measured: attribute batches whose first entry is an option class, plus
    // container-level attributes) — a narrowed filter misses the flip, and the
    // visible-index gate below already filters the noise.
    pending.observer.observe(container, { childList: true, subtree: true, attributes: true });
    pending.timer = setTimeout(stop, 2000);
  }
  function stop() {
    pending.observer?.disconnect();
    pending.observer = null;
    pending.index = -1;
    pending.container = null;
    pending.goesBack = false;
    clearTimeout(pending.timer);
  }
  function disarm() { stop(); abortSession(); }

  function onAction(event) {
    const next = event.target.closest?.(NEXT);
    if (next && !next.closest(EXCLUDED) && !next.matches(LAST_STEP)) {
      arm(next, false);
      return;
    }
    const back = event.target.closest?.(BACK);
    if (back && !back.closest(EXCLUDED)) arm(back, true);
  }
  function arm(button, goesBack) {
    const container = button.closest('.mw-group__container');
    if (!container) return;
    const index = activeIndex(container.querySelectorAll(':scope > .mw-group'));
    if (index >= 0) watch(index, container, goesBack);
  }

  window.addEventListener('click', onAction, true);
  // The button is a focusable div, so keyboard activation arrives as keydown;
  // Space also pages the document, which counts as a user gesture.
  window.addEventListener('keydown', event => {
    if (event.key === 'Enter' || event.key === ' ') {
      onAction(event);
      if (event.key === ' ') onUserIntent();
    } else if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End']
        .includes(event.key)) onUserIntent();
  }, { capture: true });
  document.addEventListener('shopify:section:unload', disarm);
})();
